import React, { useEffect } from "react";
import {
  MessageSquare,
  X,
  Mail,
  User,
  Building2,
  Frown,
  Meh,
  Smile,
  Laugh,
} from "lucide-react";
import type { FeedbackItem, FeedbackRating, FeedbackStatus } from "../api/feedbackApi";
import "../styles/feedback-notification.css";

function getRatingIcon(rating: FeedbackRating) {
  switch (rating) {
    case "bad":
      return <Frown size={22} />;
    case "okay":
      return <Meh size={22} />;
    case "good":
      return <Smile size={22} />;
    case "amazing":
      return <Laugh size={22} />;
    default:
      return <Smile size={22} />;
  }
}

interface FeedbackDetailModalProps {
  feedback: FeedbackItem | null;
  onClose: () => void;
  onStatusChange: (id: string, newStatus: FeedbackStatus) => void;
}

export default function FeedbackDetailModal({
  feedback,
  onClose,
  onStatusChange,
}: FeedbackDetailModalProps) {
  useEffect(() => {
    if (!feedback) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [feedback, onClose]);

  if (!feedback) return null;

  return (
    <div
      className="feedback-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="feedback-modal-box" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="feedback-modal-header">
          <div className="feedback-modal-header-left">
            <div className="feedback-modal-icon-badge">
              <MessageSquare size={20} />
            </div>
            <div>
              <h3 className="feedback-modal-title">Feedback Details</h3>
              <p className="feedback-modal-subtitle">
                Submitted on {new Date(feedback.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="feedback-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="feedback-modal-body">
          {/* Rating Banner */}
          <div className={`feedback-rating-highlight-box ${feedback.rating}`}>
            <div className="rating-highlight-left">
              <div className="rating-highlight-icon">
                {getRatingIcon(feedback.rating)}
              </div>
              <span className="rating-highlight-text">
                Rating: {feedback.rating.toUpperCase()}
              </span>
            </div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "rgba(255,255,255,0.8)",
              }}
            >
              Status: {feedback.status}
            </span>
          </div>

          {/* Submitter Ribbon */}
          <div className="feedback-detail-ribbon">
            <div className="feedback-ribbon-item">
              <span className="feedback-ribbon-label">Client Name</span>
              <span className="feedback-ribbon-val">
                <User size={13} style={{ color: "#818cf8" }} />
                {feedback.user_name || "Anonymous"}
              </span>
            </div>
            <div className="feedback-ribbon-item">
              <span className="feedback-ribbon-label">Client Email</span>
              <span className="feedback-ribbon-val">
                <Mail size={13} style={{ color: "#818cf8" }} />
                {feedback.user_email || "N/A"}
              </span>
            </div>
            <div className="feedback-ribbon-item">
              <span className="feedback-ribbon-label">Role</span>
              <span className="feedback-ribbon-val">
                {feedback.user_role}
              </span>
            </div>
            <div className="feedback-ribbon-item">
              <span className="feedback-ribbon-label">Organization</span>
              <span className="feedback-ribbon-val">
                <Building2 size={13} style={{ color: "#818cf8" }} />
                {feedback.user_organization || "Threatactix Community"}
              </span>
            </div>
          </div>

          {/* Suggestions Section */}
          <div className="feedback-message-section">
            <span className="feedback-message-heading">
              Client Suggestions / Feedback Message
            </span>
            <div className="feedback-message-content">
              {feedback.suggestions || "No written suggestions provided."}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="feedback-modal-footer">
          <div className="feedback-status-switch-wrap">
            <button
              type="button"
              className={`feedback-status-btn ${
                feedback.status === "unread" ? "is-current" : ""
              }`}
              onClick={() => onStatusChange(feedback.id, "unread")}
            >
              Unread
            </button>
            <button
              type="button"
              className={`feedback-status-btn ${
                feedback.status === "reviewed" ? "is-current" : ""
              }`}
              onClick={() => onStatusChange(feedback.id, "reviewed")}
            >
              Reviewed
            </button>
            <button
              type="button"
              className={`feedback-status-btn ${
                feedback.status === "archived" ? "is-current" : ""
              }`}
              onClick={() => onStatusChange(feedback.id, "archived")}
            >
              Archive
            </button>
          </div>

          {feedback.user_email && (
            <a
              href={`mailto:${feedback.user_email}?subject=Threatactix%20Feedback%20Follow-up&body=Hi%20${encodeURIComponent(
                feedback.user_name || "there"
              )},%0D%0A%0D%0AThank%20you%20for%20your%20feedback%20regarding%20Threatactix.%0D%0A%0D%0A`}
              className="feedback-email-reply-btn"
            >
              <Mail size={13} />
              <span>Reply via Email</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
